import { useState, useCallback } from 'react';

interface ImageGalleryProps {
  images: string[];
  productName: string;
  tag?: string;
}

export default function ImageGallery({ images, productName, tag }: ImageGalleryProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const goTo = useCallback((index: number) => {
    const total = images.length;
    setActiveIndex((index + total) % total);
  }, [images.length]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'ArrowLeft') goTo(activeIndex - 1);
    if (e.key === 'ArrowRight') goTo(activeIndex + 1);
    if (e.key === 'Escape') setLightboxOpen(false);
  }, [activeIndex, goTo]);

  return (
    <>
      <div className="w-full flex flex-col gap-4" onKeyDown={handleKeyDown} tabIndex={0}>
        {/* Main Image */}
        <div
          className="relative w-full h-[420px] md:h-[560px] rounded-2xl overflow-hidden bg-white cursor-zoom-in group"
          onClick={() => setLightboxOpen(true)}
        >
          <img
            key={activeIndex}
            src={images[activeIndex]}
            alt={`${productName} view ${activeIndex + 1}`}
            title={`${productName} - sofa bed view ${activeIndex + 1}`}
            className="w-full h-full object-cover object-top transition-opacity duration-300"
          />

          {/* Tag badge */}
          {tag && (
            <span className="absolute top-4 left-4 bg-[#3a3228] text-white text-[10px] font-semibold uppercase tracking-wider px-3 py-1.5 rounded-full z-10">
              {tag}
            </span>
          )}

          {/* Image count badge */}
          <span className="absolute top-4 right-4 bg-black/40 text-white text-[11px] font-medium px-2.5 py-1 rounded-full backdrop-blur-sm">
            {activeIndex + 1} / {images.length}
          </span>

          {/* Zoom hint */}
          <div className="absolute bottom-4 right-4 bg-black/30 text-white text-[11px] px-2.5 py-1 rounded-full backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center gap-1.5">
            <i className="ri-zoom-in-line text-xs" />
            <span>Expand</span>
          </div>

          {/* Prev/Next Arrows */}
          {images.length > 1 && (
            <>
              <button
                onClick={(e) => { e.stopPropagation(); goTo(activeIndex - 1); }}
                className="whitespace-nowrap absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm text-[#2c2420] hover:bg-white transition-all duration-200 cursor-pointer opacity-0 group-hover:opacity-100"
                aria-label="Previous image"
              >
                <i className="ri-arrow-left-s-line text-lg" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); goTo(activeIndex + 1); }}
                className="whitespace-nowrap absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center rounded-full bg-white/80 backdrop-blur-sm text-[#2c2420] hover:bg-white transition-all duration-200 cursor-pointer opacity-0 group-hover:opacity-100"
                aria-label="Next image"
              >
                <i className="ri-arrow-right-s-line text-lg" />
              </button>
            </>
          )}
        </div>

        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="flex gap-2.5 overflow-x-auto pb-1 scrollbar-hide">
            {images.map((img, idx) => (
              <button
                key={idx}
                onClick={() => setActiveIndex(idx)}
                className={`whitespace-nowrap flex-shrink-0 w-[76px] h-[76px] rounded-xl overflow-hidden border-2 transition-all duration-200 cursor-pointer ${
                  idx === activeIndex
                    ? 'border-[#8B6348] ring-1 ring-[#8B6348]/30'
                    : 'border-transparent hover:border-stone-300'
                }`}
                aria-label={`View image ${idx + 1}`}
              >
                <img
                  src={img}
                  alt={`${productName} thumbnail ${idx + 1}`}
                  className="w-full h-full object-cover object-top"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center"
          onClick={() => setLightboxOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'ArrowLeft') goTo(activeIndex - 1);
            if (e.key === 'ArrowRight') goTo(activeIndex + 1);
            if (e.key === 'Escape') setLightboxOpen(false);
          }}
          tabIndex={0}
        >
          {/* Close */}
          <button
            onClick={() => setLightboxOpen(false)}
            className="whitespace-nowrap absolute top-5 right-5 w-10 h-10 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer z-10"
          >
            <i className="ri-close-line text-lg" />
          </button>

          {/* Counter */}
          <span className="absolute top-5 left-1/2 -translate-x-1/2 text-white/60 text-sm">
            {activeIndex + 1} / {images.length}
          </span>

          {/* Main lightbox image */}
          <div
            className="relative w-full max-w-4xl mx-8 flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={images[activeIndex]}
              alt={`${productName} lightbox view ${activeIndex + 1}`}
              className="max-h-[80vh] max-w-full rounded-xl object-contain"
            />

            {images.length > 1 && (
              <>
                <button
                  onClick={() => goTo(activeIndex - 1)}
                  className="whitespace-nowrap absolute -left-14 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-s-line text-xl" />
                </button>
                <button
                  onClick={() => goTo(activeIndex + 1)}
                  className="whitespace-nowrap absolute -right-14 w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-right-s-line text-xl" />
                </button>
              </>
            )}
          </div>

          {/* Lightbox thumbnails */}
          {images.length > 1 && (
            <div
              className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2"
              onClick={(e) => e.stopPropagation()}
            >
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveIndex(idx)}
                  className={`whitespace-nowrap w-14 h-14 rounded-lg overflow-hidden border-2 transition-all duration-200 cursor-pointer ${
                    idx === activeIndex ? 'border-white' : 'border-white/20 hover:border-white/50'
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover object-top" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}
